import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import ImageUpload from './ImageUpload';
import AddPositionModal from './AddPositionModal';
import { API_BASE_URL } from '../utils/env.js';
import { useUser } from '../contexts/UserContext';

const AddAchievementModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  achievement = null,
  initialDraft = null,
  onRefreshAchievements
}) => {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    description: '',
    impact: '',
    status: 'Work Achievement',
    position_id: '',
    skills: []
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [showAddPositionModal, setShowAddPositionModal] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [showSkillInput, setShowSkillInput] = useState(false);

  // Image-related state
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageError, setImageError] = useState('');

  const isEditing = !!(achievement && achievement.id);

  const achievementTypeOptions = [
    'Work Achievement',
    'Personal Achievement',
    'Learning Achievement'
  ];

  const achievementStatusOptions = [
    { value: 'Draft', label: '📝 Draft', emoji: '📝' },
    { value: 'Active', label: '🔄 Active', emoji: '🔄' },
    { value: 'Ready', label: '✅ Ready', emoji: '✅' },
    { value: 'Published', label: '🌟 Published', emoji: '🌟' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadAvailableSkills();
      loadAvailablePositions();
    }
  }, [isOpen]);

  useEffect(() => {
    // Only setup when modal opens
    if (!isOpen) return;
    
    if (achievement && achievement.id) {
      // Editing existing achievement - use actual values
      setFormData({
        title: achievement.title || '',
        date: achievement.date ? achievement.date.split('T')[0] : '',
        description: achievement.description || '',
        impact: achievement.impact || '',
        status: achievement.achievement_type || achievement.status || 'Work Achievement',
        position_id: achievement.position_id || '',
        skills: achievement.skills || []
      });
      setImages(achievement.images || []);
    } else {
      // Creating new achievement - always start fresh, except for a
      // pre-filled description when converting a "Not an achievement yet?" placeholder
      setFormData({
        title: '',
        date: '',
        description: initialDraft?.description || '',
        impact: '',
        status: 'Work Achievement',
        position_id: achievement?.position_id || '',
        skills: []
      });
      setImages([]);
    }
    
    // Reset other state
    setNewImages([]);
    setDeletedImageIds([]);
    setErrors({});
    setImageError('');
  }, [achievement, isOpen, initialDraft]);

  // Reset form completely when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        date: '',
        description: '',
        impact: '',
        status: 'Work Achievement',
        skills: []
      });
    }
  }, [isOpen]);

  const loadAvailableSkills = async () => {
    try {
      const skills = await apiService.getAllSkills();
      setAvailableSkills(skills);
    } catch (error) {
      console.error('Error loading skills:', error);
    }
  };

  const loadAvailablePositions = async () => {
    try {
      const response = await apiService.getUserPositions();
      setAvailablePositions(response.positions || []);
    } catch (error) {
      console.error('Error loading positions:', error);
      setAvailablePositions([]);
    }
  };

  const handleAddPositionSave = async (formData) => {
    const created = await apiService.createPosition(formData);
    const newPosition = created.position || created;
    await loadAvailablePositions();
    setFormData(prev => ({ ...prev, position_id: newPosition.id }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters long';
    }

    if (!formData.date.trim()) {
      newErrors.date = 'Target Completion date is required';
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.date)) {
        newErrors.date = 'Please enter a valid date (yyyy-mm-dd)';
      }
    }

    // Description is optional, but if provided, it should have minimum length
    if (formData.description.trim() && formData.description.length < 10) {
      newErrors.description = 'Story must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleAddNewSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
      setShowSkillInput(false);
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  // Image handling functions
  const handleNewImages = (files) => {
    const imageObjects = files.map(file => ({
      file,
      originalName: file.name,
      isNew: true
    }));
    setNewImages(prev => [...prev, ...imageObjects]);
  };

  const handleImageError = (error) => {
    setImageError(error);
  };

  const handleImageDelete = (imageIdOrIndex) => {
    if (typeof imageIdOrIndex === 'string') {
      // Deleting existing image
      setDeletedImageIds(prev => [...prev, imageIdOrIndex]);
      setImages(prev => prev.filter(img => img.id !== imageIdOrIndex));
    } else {
      // Deleting new image (by index)
      setNewImages(prev => prev.filter((_, index) => index !== imageIdOrIndex));
    }
  };

  const uploadImages = async (achievementId) => {
    if (newImages.length === 0) return [];

    setIsUploadingImages(true);
    const formData = new FormData();
    
    console.log(`Uploading ${newImages.length} images to achievement ${achievementId}`);
    
    newImages.forEach((imageObj, index) => {
      console.log(`Adding image ${index + 1}: ${imageObj.file.name}, size: ${imageObj.file.size}, type: ${imageObj.file.type}`);
      formData.append('images', imageObj.file);
    });

    try {
      console.log('Sending image upload request...');
      const uploadUrl = `${API_BASE_URL}/images/upload/${achievementId}`;
      console.log(`Upload URL: ${uploadUrl}`);
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
        // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
      });

      console.log(`Upload response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload failed with response:', errorText);
        throw new Error(`Failed to upload images: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      return result.images || [];
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setIsUploadingImages(false);
    }
  };

  const deleteImages = async (achievementId) => {
    if (deletedImageIds.length === 0) return;
    
    const deletePromises = deletedImageIds.map(imageId => {
      const deleteUrl = `${API_BASE_URL}/images/${imageId}/achievement/${achievementId}`;
      console.log(`Deleting image: ${deleteUrl}`);
      return fetch(deleteUrl, {
        method: 'DELETE'
      });
    });

    try {
      await Promise.all(deletePromises);
      console.log(`Successfully deleted ${deletedImageIds.length} images`);
    } catch (error) {
      console.error('Error deleting images:', error);
      // Don't throw - deletion errors shouldn't prevent saving
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      let achievementId;
      
      // First, save the achievement data
      if (isEditing) {
        await onSave(formData);
        achievementId = achievement.id;
      } else {
        const result = await onSave(formData);
        achievementId = result?.id || result;
      }

      // Handle image operations after saving achievement
      // Don't let image failures block the achievement save
      if (achievementId) {
        try {
          await Promise.all([
            deleteImages(achievementId),
            uploadImages(achievementId)
          ]);
          console.log('Images processed successfully, refreshing achievements...');
        } catch (imageError) {
          console.error('Image operation failed:', imageError);
          // Show a warning but don't prevent the modal from closing
          setErrors({ 
            submit: 'Achievement saved successfully, but there was an issue with image upload. You can edit the achievement to try uploading images again.' 
          });
          // Clear the error after 5 seconds and close the modal
          setTimeout(() => {
            setErrors({});
            onClose();
            if (onRefreshAchievements) onRefreshAchievements();
          }, 5000);
          return;
        }
      }
      
      onClose();
      if (onRefreshAchievements) onRefreshAchievements(); // Refresh achievements after successful save/update
    } catch (error) {
      console.error('Error saving achievement:', error);
      setErrors({ submit: 'Failed to save achievement. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form state when closing
      setFormData({
        title: '',
        date: '',
        description: '',
        impact: '',
        status: 'Work Achievement',
        skills: []
      });
      setImages([]);
      setNewImages([]);
      setDeletedImageIds([]);
      setErrors({});
      setImageError('');
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-testid="achievement-modal"
    >
      <div className="achievement-modal bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Achievement' : 'Capture a win'}
            </h2>
            {!isEditing && (
              <p className="text-sm text-gray-400 mt-0.5">Answer 3 questions — takes 2 minutes.</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
            disabled={isSubmitting}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {errors.submit}
            </div>
          )}

          {/* NEW: Conversational form for new achievements */}
          {!isEditing ? (
            <>
              {/* Q1: What did you do? */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">
                  What did you do?
                </label>
                <p className="text-xs text-gray-400 mb-2">Don't overthink it — write it how you'd explain it to a friend.</p>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="e.g. I led the migration of our billing system to a new provider. I coordinated with 3 teams, wrote the runbook, and managed the cutover weekend..."
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 resize-none transition-colors ${
                    errors.description ? 'border-red-300' : 'border-gray-200'
                  }`}
                  disabled={isSubmitting}
                  maxLength={2000}
                />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
              </div>

              {/* Q2: What happened because of it? */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">
                  What happened because of it?
                </label>
                <p className="text-xs text-gray-400 mb-2">Did it save time, reduce costs, or improve something? Even "my manager was happy" counts.</p>
                <input
                  type="text"
                  name="impact"
                  value={formData.impact}
                  onChange={handleInputChange}
                  placeholder="e.g. Reduced billing errors by 80%, saved ~4 hrs/week of manual work"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 transition-colors"
                  disabled={isSubmitting}
                  maxLength={500}
                />
              </div>

              {/* Q3: Give it a short title */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">
                  Give it a short title
                </label>
                <p className="text-xs text-gray-400 mb-2">A quick label so you can find it later.</p>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g. Billing system migration"
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 transition-colors ${
                    errors.title ? 'border-red-300' : 'border-gray-200'
                  }`}
                  disabled={isSubmitting}
                  maxLength={200}
                />
                {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
              </div>

              {/* Date + Type row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    When did this happen? <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 transition-colors ${
                      errors.date ? 'border-red-300' : 'border-gray-200'
                    }`}
                    disabled={isSubmitting}
                    data-testid="date-input"
                  />
                  {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-1">Type</label>
                  <div className="flex gap-1.5 flex-wrap pt-0.5">
                    {['Work', 'Personal', 'Learning'].map(type => {
                      const val = `${type} Achievement`;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status: val }))}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            formData.status === val
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}
                          disabled={isSubmitting}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Related position — collapsed/optional */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-500">
                    Related position <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowAddPositionModal(true)}
                    className="text-xs text-gray-400 hover:text-gray-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    + Add position
                  </button>
                </div>
                <select
                  name="position_id"
                  value={formData.position_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 transition-colors bg-white"
                  disabled={isSubmitting}
                >
                  <option value="">None</option>
                  {availablePositions.map(position => (
                    <option key={position.id} value={position.id}>
                      {position.position_title} at {position.company_name}
                      {position.is_current ? ' (Current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            /* EDIT MODE: structured form */
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 ${errors.title ? 'border-red-300' : 'border-gray-200'}`}
                  disabled={isSubmitting}
                  maxLength={200}
                />
                {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">What happened</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 resize-none ${errors.description ? 'border-red-300' : 'border-gray-200'}`}
                  disabled={isSubmitting}
                  maxLength={2000}
                />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                <input
                  type="text"
                  name="impact"
                  value={formData.impact}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400"
                  disabled={isSubmitting}
                  maxLength={500}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 ${errors.date ? 'border-red-300' : 'border-gray-200'}`}
                    disabled={isSubmitting}
                    data-testid="date-input"
                  />
                  {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <div className="flex gap-1.5 flex-wrap pt-0.5">
                    {['Work', 'Personal', 'Learning'].map(type => {
                      const val = `${type} Achievement`;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status: val }))}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            formData.status === val
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                          }`}
                          disabled={isSubmitting}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700">Related position <span className="text-gray-400 font-normal">(optional)</span></label>
                  <button
                    type="button"
                    onClick={() => setShowAddPositionModal(true)}
                    className="text-xs text-gray-400 hover:text-gray-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    + Add position
                  </button>
                </div>
                <select
                  name="position_id"
                  value={formData.position_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400 bg-white"
                  disabled={isSubmitting}
                >
                  <option value="">None</option>
                  {availablePositions.map(position => (
                    <option key={position.id} value={position.id}>
                      {position.position_title} at {position.company_name}
                      {position.is_current ? ' (Current)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Images (edit mode only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Images <span className="text-gray-400 font-normal">(optional)</span></label>
                <ImageUpload
                  images={[...images, ...newImages]}
                  onImagesChange={handleNewImages}
                  onImageDelete={handleImageDelete}
                  onError={handleImageError}
                  maxFiles={5}
                  disabled={isSubmitting || isUploadingImages}
                  achievementId={achievement?.id}
                  existingImages={images}
                />
                {isUploadingImages && <p className="mt-1 text-xs text-blue-600">Uploading images...</p>}
                {imageError && <p className="mt-1 text-xs text-red-600">{imageError}</p>}
              </div>
            </>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (isEditing ? 'Save changes' : 'Log achievement')}
            </button>
          </div>
        </form>
      </div>
    </div>
    <AddPositionModal
      isOpen={showAddPositionModal}
      onClose={() => setShowAddPositionModal(false)}
      onSave={handleAddPositionSave}
    />
    </>
  );
};

export default AddAchievementModal; 